<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { api, messageFrom } from '../api'
import type { GenerationModelSetting, VoiceModelSetting, VoiceSpeakerSetting } from '../types'

const emit = defineEmits<{ navigate: [path: string] }>()
const models = ref<GenerationModelSetting[]>([])
const generationModel = ref('')
const generationModelFallback = ref('')
const generationModelFallback2 = ref('')
const savedModel = ref('')
const savedModelFallback = ref('')
const savedModelFallback2 = ref('')
const voiceModels = ref<VoiceModelSetting[]>([])
const voiceSpeakers = ref<VoiceSpeakerSetting[]>([])
const voiceModel = ref('')
const savedVoiceModel = ref('')
const testVoiceSpeakerId = ref('')
// Средняя длина реплики спикера: цена за знак сама по себе ничего не говорит,
// а «сколько стоит один ответ» — говорит.
const TYPICAL_ANSWER_CHARS = 650

function voiceModelCost(model: VoiceModelSetting): string {
  const cents = model.centsPerChar * TYPICAL_ANSWER_CHARS
  return `${cents.toFixed(cents < 1 ? 2 : 1).replace('.', ',')} ¢ за ответ`
}

const loading = ref(true)
const saving = ref(false)
type ModelSlot = 'primary' | 'fallback' | 'fallback2'
interface ModelTestResult { response: string; durationMs: number }
const testingModelSlot = ref<ModelSlot | null>(null)
const modelTestResults = ref<Record<ModelSlot, ModelTestResult | null>>({
  primary: null,
  fallback: null,
  fallback2: null,
})
const testingVoice = ref(false)
const error = ref('')
const testVoiceAudio = ref('')
const testVoiceDurationMs = ref<number | null>(null)

async function load(): Promise<void> {
  loading.value = true
  try {
    const response = await api<{
      generationModel: string
      generationModelFallback: string
      generationModelFallback2: string
      models: GenerationModelSetting[]
      voiceModel: string
      voiceModels: VoiceModelSetting[]
      voiceSpeakers: VoiceSpeakerSetting[]
    }>('/api/admin/settings')
    models.value = response.models
    generationModel.value = response.generationModel
    generationModelFallback.value = response.generationModelFallback
    generationModelFallback2.value = response.generationModelFallback2
    savedModel.value = response.generationModel
    savedModelFallback.value = response.generationModelFallback
    savedModelFallback2.value = response.generationModelFallback2
    voiceModels.value = response.voiceModels
    voiceModel.value = response.voiceModel
    savedVoiceModel.value = response.voiceModel
    voiceSpeakers.value = response.voiceSpeakers
    if (!testVoiceSpeakerId.value) testVoiceSpeakerId.value = response.voiceSpeakers[0]?.id ?? ''
    error.value = ''
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function save(): Promise<void> {
  if (saving.value || !generationModel.value || !voiceModel.value) return
  saving.value = true
  try {
    await api('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify({
        generationModel: generationModel.value,
        generationModelFallback: generationModelFallback.value,
        generationModelFallback2: generationModelFallback2.value,
        voiceModel: voiceModel.value,
      }),
    })
    savedModel.value = generationModel.value
    savedModelFallback.value = generationModelFallback.value
    savedModelFallback2.value = generationModelFallback2.value
    savedVoiceModel.value = voiceModel.value
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { saving.value = false }
}

async function testVoice(): Promise<void> {
  if (testingVoice.value || !voiceModel.value || !testVoiceSpeakerId.value) return
  testingVoice.value = true
  testVoiceAudio.value = ''
  testVoiceDurationMs.value = null
  try {
    const response = await api<{ audio: string; durationMs: number }>('/api/admin/settings/test-voice', {
      method: 'POST',
      body: JSON.stringify({
        voiceModel: voiceModel.value,
        speakerId: testVoiceSpeakerId.value,
      }),
    })
    testVoiceAudio.value = response.audio
    testVoiceDurationMs.value = response.durationMs
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { testingVoice.value = false }
}

async function testModel(modelKey: string, slot: ModelSlot): Promise<void> {
  if (testingModelSlot.value || !modelKey) return
  testingModelSlot.value = slot
  modelTestResults.value[slot] = null
  try {
    const response = await api<{ response: string; durationMs: number }>('/api/admin/settings/test', {
      method: 'POST',
      body: JSON.stringify({ generationModel: modelKey }),
    })
    modelTestResults.value[slot] = response
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { testingModelSlot.value = null }
}

onMounted(load)
</script>

<template>
  <main class="page page--settings">
    <button class="back-link" type="button" @click="emit('navigate', '/admin')">← К папкам спикеров</button>
    <header class="page-heading">
      <p class="eyebrow">Система</p>
      <h1>Настройки</h1>
    </header>

    <p v-if="loading" class="quiet-message">Загружаем настройки…</p>
    <div v-else class="settings-sections">
      <section class="settings-card">
        <label for="generation-model">Модель</label>
        <select id="generation-model" v-model="generationModel">
          <option
            v-for="model in models"
            :key="model.key"
            :value="model.key"
            :disabled="!model.available"
          >
            {{ model.label }}{{ model.available ? '' : ` — ${model.unavailableReason}` }}
          </option>
        </select>
        <button
          class="button button--quiet"
          type="button"
          :disabled="Boolean(testingModelSlot)"
          @click="testModel(generationModel, 'primary')"
        >
          {{ testingModelSlot === 'primary' ? 'Проверяем…' : 'Проверить' }}
        </button>
        <div v-if="modelTestResults.primary" class="test-result">
          <strong>Ответ за {{ modelTestResults.primary.durationMs }} мс</strong>
          <p>{{ modelTestResults.primary.response }}</p>
        </div>

        <label for="generation-model-fallback">Запасная модель</label>
        <select id="generation-model-fallback" v-model="generationModelFallback">
          <option value="">не использовать</option>
          <option
            v-for="model in models"
            :key="model.key"
            :value="model.key"
            :disabled="!model.available"
          >
            {{ model.label }}{{ model.available ? '' : ` — ${model.unavailableReason}` }}
          </option>
        </select>
        <button
          class="button button--quiet"
          type="button"
          :disabled="Boolean(testingModelSlot) || !generationModelFallback"
          @click="testModel(generationModelFallback, 'fallback')"
        >
          {{ testingModelSlot === 'fallback' ? 'Проверяем…' : 'Проверить' }}
        </button>
        <div v-if="modelTestResults.fallback" class="test-result">
          <strong>Ответ за {{ modelTestResults.fallback.durationMs }} мс</strong>
          <p>{{ modelTestResults.fallback.response }}</p>
        </div>

        <label for="generation-model-fallback-2">Запасная модель 2</label>
        <select id="generation-model-fallback-2" v-model="generationModelFallback2">
          <option value="">не использовать</option>
          <option
            v-for="model in models"
            :key="model.key"
            :value="model.key"
            :disabled="!model.available"
          >
            {{ model.label }}{{ model.available ? '' : ` — ${model.unavailableReason}` }}
          </option>
        </select>
        <button
          class="button button--quiet"
          type="button"
          :disabled="Boolean(testingModelSlot) || !generationModelFallback2"
          @click="testModel(generationModelFallback2, 'fallback2')"
        >
          {{ testingModelSlot === 'fallback2' ? 'Проверяем…' : 'Проверить' }}
        </button>
        <div v-if="modelTestResults.fallback2" class="test-result">
          <strong>Ответ за {{ modelTestResults.fallback2.durationMs }} мс</strong>
          <p>{{ modelTestResults.fallback2.response }}</p>
        </div>
        <p class="settings-hint">
          Модели применяются по порядку для ответов, границ речи и карточек спикеров.
        </p>
      </section>

      <section class="settings-card">
        <label for="voice-model">Модель озвучки</label>
        <select id="voice-model" v-model="voiceModel">
          <option v-for="model in voiceModels" :key="model.key" :value="model.key">
            {{ model.label }} — {{ voiceModelCost(model) }}, {{ model.typicalSeconds }} с на фразу
          </option>
        </select>
        <p class="settings-hint">
          Цена посчитана на ответ средней длины, секунды — замер синтеза одной фразы
          на боевом сервере. В списке только модели, которые говорят клонированным
          голосом спикера.
        </p>
        <label for="test-voice-speaker">Голос для проверки</label>
        <select id="test-voice-speaker" v-model="testVoiceSpeakerId" :disabled="!voiceSpeakers.length">
          <option v-for="voiceSpeaker in voiceSpeakers" :key="voiceSpeaker.id" :value="voiceSpeaker.id">
            {{ voiceSpeaker.name }}
          </option>
        </select>
        <button
          class="button button--quiet"
          type="button"
          :disabled="testingVoice || !testVoiceSpeakerId"
          @click="testVoice"
        >
          {{ testingVoice ? 'Синтезируем…' : 'Проверить озвучку' }}
        </button>
        <div v-if="testVoiceAudio" class="test-result">
          <strong>Озвучка за {{ testVoiceDurationMs }} мс</strong>
          <audio controls autoplay :src="testVoiceAudio">Браузер не поддерживает аудиоплеер.</audio>
        </div>
      </section>

      <button
        class="button button--primary"
        type="button"
        :disabled="saving || (generationModel === savedModel
          && generationModelFallback === savedModelFallback
          && generationModelFallback2 === savedModelFallback2
          && voiceModel === savedVoiceModel)"
        @click="save"
      >
        {{ saving ? 'Сохраняем…' : 'Сохранить настройки' }}
      </button>
    </div>
    <p v-if="error" class="field-error" role="alert">{{ error }}</p>
  </main>
</template>
